import { DataLoadError, Loader, useGlobalModalContext } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { ReactElement, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { MODAL_TYPES } from '../../../components/GlobalModal/GlobalModal';
import { useDocumentTitle } from '../../../hooks/common';
import useFetchCases from '../../../hooks/dockets/useFetchCases';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { DocketCase } from '../../../models/CompanyDockets';
import { CompanyPageParams } from '../../../models/CompanyPageParams';
import { hasPermissionToDockets } from '../../../utils/permissionsUtils';
import { CompanyOutletContext } from '../Company';
import DocketCaseDocumentsTable from './DocketCaseDocumentsTable';
import DocketCaseInfo from './DocketCaseInfo';

import styles from './CompanyDocketsTab.module.scss';

const cx = classNames.bind(styles);

const DOCUMENTS_PER_PAGE = [100, 200];

const CompanyDocketsTab = (): ReactElement | null => {
  const { issuerId } = useParams() as CompanyPageParams;
  const { company } = useOutletContext<CompanyOutletContext>();
  const documentTitle = company ? `${company.displayName}: Court Dockets` : '';
  useDocumentTitle(documentTitle);
  const { showModal, closeModal } = useGlobalModalContext();
  const { data: userPermissions } = useFetchUserPermissions();
  const [selectedDocketCase, setSelectedDocketCase] = useState<DocketCase | undefined>();
  const {
    data: docketCases,
    isLoading: isLoadingDocketCases,
    isError: isDocketCasesError,
    refetch,
  } = useFetchCases(issuerId, hasPermissionToDockets(userPermissions));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DOCUMENTS_PER_PAGE[0]);

  useEffect(() => {
    if (docketCases) {
      setSelectedDocketCase(docketCases.find((c) => c.defaultCase));
    }
  }, [docketCases]);

  const onPageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const confirmDocketCase = (docketCase) => {
    setSelectedDocketCase(docketCase);
    setPage(1);
    closeModal();
  };

  const viewRelatedCases = () => {
    showModal(MODAL_TYPES.DOCKET_VIEW_CASES, {
      docketCases,
      selectedDocketCase,
      onConfirm: confirmDocketCase,
    });
  };

  const renderContent = () => {
    if (isLoadingDocketCases) {
      return (
        <div className={cx('state-wrapper')}>
          <Loader position="center" />
        </div>
      );
    }

    if (isDocketCasesError) {
      return (
        <div className={cx('state-wrapper')}>
          <DataLoadError onRetryClick={refetch} />
        </div>
      );
    }

    if (selectedDocketCase) {
      return (
        <>
          <header>
            <DocketCaseInfo docketCase={selectedDocketCase} viewRelatedCases={viewRelatedCases} />
          </header>
          <DocketCaseDocumentsTable
            docketCase={selectedDocketCase}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            pageSizes={DOCUMENTS_PER_PAGE}
            onPageSizeChange={onPageSizeChange}
          />
        </>
      );
    }

    return null;
  };

  return (
    <section className={cx('body')} data-testid="company-dockets-tab">
      {renderContent()}
    </section>
  );
};

export default CompanyDocketsTab;
